// @system — Blog API client (server: api/@system/blog). Public reads, the admin CRUD, the
// generation pipeline and the settings row all go through the shared HTTP client
// (lib/@system/api.js): cookie session, CSRF header on mutations, `ApiError` on non-2xx.
// The shapes below mirror db/repos/@system/BlogRepo.js + migration 036_blog_engine; the
// external `POST /api/blog/posts` route (API key) is documented on the Integrations tab and in
// docs/API.md — the dashboard never calls it.
import { api } from '../../lib/@system/api.js'

// ─── Shapes ──────────────────────────────────────────────────────────────────

/** @typedef {'draft' | 'published' | 'scheduled' | 'archived'} BlogStatus */
/** How a post came to exist: written in the editor, generated by the pipeline, pushed through the API, or seeded. */
/** @typedef {'manual' | 'generated' | 'api' | 'seed'} BlogSource */
/** @typedef {'hero' | 'figure' | 'diagram' | 'upload'} BlogImageKind */

/**
 * One entry of `blog_posts.images`.
 * @typedef {object} BlogImage
 * @property {string} id            Stable id — also the `image:<id>` placeholder in the markdown
 * @property {string} url           Absolute public URL (StorageAdapter)
 * @property {string} alt
 * @property {string | null} [caption]
 * @property {BlogImageKind} kind
 * @property {number} [width]
 * @property {number} [height]
 * @property {string | null} [provider]  orkosi | openai | replicate | svg | upload
 */

/**
 * `generation_meta` of a generated post.
 * @typedef {object} BlogGenerationMeta
 * @property {number} [run_id]
 * @property {string} [model]
 * @property {string} [provider]
 * @property {number} [cost_usd]
 * @property {Record<string, unknown>} [plan]
 * @property {Record<string, unknown>} [seo_report]
 */

/**
 * `blog_posts` row. Public list routes omit `content`/`content_html`; the single-post
 * routes return the full row. Dates arrive as ISO strings.
 * @typedef {object} BlogPostRow
 * @property {number} id
 * @property {string} slug
 * @property {string} title
 * @property {string | null} [excerpt]
 * @property {string} [content]                 Markdown source
 * @property {string | null} [content_html]     Sanitized HTML rendered from `content` at write time
 * @property {string | null} [category]
 * @property {string | null} [author]
 * @property {string[] | null} [tags]
 * @property {BlogStatus} [status]              Admin routes only
 * @property {boolean} [featured]
 * @property {number | null} [reading_time]     Minutes
 * @property {string | null} [cover_image]      1600×900 hero URL
 * @property {string | null} [thumbnail_image]  640×360 card URL
 * @property {string | null} [og_image]         1200×630 social card URL
 * @property {BlogImage[]} [images]
 * @property {BlogSource} [source]
 * @property {string | null} [external_source]  e.g. 'rankarilla'
 * @property {string | null} [external_id]
 * @property {string | null} [keyword]          Primary SEO keyword
 * @property {string | null} [canonical_url]
 * @property {string | null} [meta_title]
 * @property {string | null} [meta_description]
 * @property {string | null} [scheduled_at]     ISO — when `status` is 'scheduled'
 * @property {string | null} [published_at]     ISO
 * @property {BlogGenerationMeta | null} [generation_meta]
 * @property {string} created_at                ISO
 * @property {string} [updated_at]              ISO
 */

/**
 * Body of `POST`/`PATCH /admin/blog[/:id]` — the writable columns (server re-renders
 * `content_html` whenever `content` changes and derives `reading_time`).
 * @typedef {object} BlogPostBody
 * @property {string} [title]
 * @property {string} [slug]
 * @property {string} [excerpt]
 * @property {string} [content]
 * @property {string} [category]
 * @property {string} [author]
 * @property {string[]} [tags]
 * @property {BlogStatus} [status]
 * @property {boolean} [featured]
 * @property {string | null} [scheduled_at]
 * @property {string | null} [published_at]
 * @property {string} [meta_title]
 * @property {string} [meta_description]
 * @property {string} [keyword]
 * @property {string | null} [canonical_url]
 * @property {string | null} [cover_image]
 * @property {BlogImage[]} [images]
 */

/** @typedef {'daily' | 'weekdays' | 'weekly' | 'manual'} BlogCadence */
/** @typedef {'professional' | 'educational' | 'thought-leadership'} BlogTone */
/** @typedef {'auto' | 'orkosi' | 'anthropic' | 'openai'} BlogTextProvider */
/** @typedef {'auto' | 'orkosi' | 'openai' | 'replicate' | 'svg'} BlogImageProvider */

/** Product page the generator may link to from an article. @typedef {{ path: string, label: string }} BlogInternalLink */

/**
 * The single `blog_settings` row (id = 1). Defaults come from brand.json `blog` the first time
 * the row is created; afterwards the row is the source of truth.
 * @typedef {object} BlogSettings
 * @property {boolean} enabled
 * @property {BlogCadence} cadence
 * @property {number} publish_hour          0–23 in `timezone`
 * @property {string} timezone              IANA zone, default 'UTC'
 * @property {boolean} auto_publish
 * @property {string[]} topics
 * @property {string[]} keywords
 * @property {string | null} audience
 * @property {BlogTone} tone
 * @property {number} word_min
 * @property {number} word_max
 * @property {BlogTextProvider} text_provider
 * @property {string | null} text_model
 * @property {BlogImageProvider} image_provider
 * @property {string | null} image_model
 * @property {string | null} default_author
 * @property {string | null} default_category
 * @property {string[] | null} categories
 * @property {BlogInternalLink[]} internal_links
 * @property {boolean} first_article_done
 * @property {string | null} next_run_at     ISO
 * @property {string | null} updated_at      ISO
 */

/** What `PATCH /admin/blog/settings` accepts — any subset of the editable columns. */
/** @typedef {Partial<Omit<BlogSettings, 'first_article_done' | 'next_run_at' | 'updated_at'>>} BlogSettingsPatch */

/**
 * Which providers the server can actually reach (env keys present) — the Settings tab greys
 * out the rest. `svg` imagery and `storage` are the always-available fallbacks.
 * @typedef {object} BlogCapabilities
 * @property {Array<Exclude<BlogTextProvider, 'auto'>>} text
 * @property {Array<Exclude<BlogImageProvider, 'auto'>>} image
 * @property {boolean} storage
 */

/** @typedef {'queued' | 'running' | 'done' | 'failed'} BlogRunStatus */
/** @typedef {'bootstrap' | 'schedule' | 'manual' | 'api'} BlogRunTrigger */

/**
 * `blog_generation_runs` row.
 * @typedef {object} BlogRun
 * @property {number} id
 * @property {BlogRunStatus} status
 * @property {BlogRunTrigger} trigger
 * @property {string | null} topic
 * @property {string | null} keyword
 * @property {number | null} post_id
 * @property {string | null} [post_slug]     Slug of the resulting post when the server joins it (optional)
 * @property {string | null} [post_title]
 * @property {string | null} error
 * @property {number | string | null} cost_usd   NUMERIC — pg may return it as a string
 * @property {string | null} model
 * @property {string | null} provider
 * @property {Record<string, unknown> | null} [meta]
 * @property {number | null} [requested_by]
 * @property {string | null} started_at    ISO
 * @property {string | null} finished_at   ISO
 * @property {string} created_at           ISO
 */

/** `GET /blog` body. @typedef {{ posts: BlogPostRow[], total: number, page: number, limit: number }} BlogListResponse */
/** `GET /admin/blog` body. @typedef {{ posts: BlogPostRow[], total?: number, page?: number, limit?: number }} AdminBlogListResponse */
/** `GET /admin/blog/settings` body. @typedef {{ settings: BlogSettings, capabilities: BlogCapabilities }} BlogSettingsResponse */

/**
 * `POST /admin/blog/:id/cover` body — exactly one of `url` / `data_base64`.
 * @typedef {object} BlogCoverInput
 * @property {string} [url]           https URL the server fetches, re-encodes and stores
 * @property {string} [data_base64]   Raw base64 (no data: prefix), ≤ 8 MB decoded
 * @property {string} [content_type]  image/* of `data_base64`
 * @property {string} [alt]
 */

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * @param {Record<string, unknown>} [params]
 * @returns {string} `?a=b` query string; empty when nothing is set
 */
function qs(params = {}) {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  if (entries.length === 0) return ''
  const search = new URLSearchParams()
  for (const [k, v] of entries) search.set(k, Array.isArray(v) ? v.join(',') : String(v))
  return `?${search.toString()}`
}

/** @param {number | string} id */
const idPath = (id) => encodeURIComponent(String(id))

// ─── Public (module blog, no auth) ───────────────────────────────────────────

/**
 * Published posts, newest first.
 * @param {{ category?: string, search?: string, page?: number, limit?: number }} [params]
 * @returns {Promise<BlogListResponse>}
 */
export const listPosts = (params) => api.get(`/blog${qs(params)}`)

/**
 * One published post by slug (full row incl. `content`, `content_html`, `images`). 404 → ApiError.
 * @param {string} slug
 * @returns {Promise<{ post: BlogPostRow }>}
 */
export const getPostBySlug = (slug) => api.get(`/blog/${encodeURIComponent(slug)}`)

/**
 * Published posts in the same category, excluding the post itself.
 * @param {string} slug
 * @param {number} [limit]
 * @returns {Promise<{ posts: BlogPostRow[] }>}
 */
export const getRelatedPosts = (slug, limit = 4) => api.get(`/blog/${encodeURIComponent(slug)}/related${qs({ limit })}`)

// ─── Admin (session, content.write) ──────────────────────────────────────────

/**
 * Every post, drafts included.
 * @param {{ status?: BlogStatus | '', page?: number, limit?: number }} [params]
 * @returns {Promise<AdminBlogListResponse>}
 */
export const adminListPosts = (params) => api.get(`/admin/blog${qs(params)}`)

/** @param {number | string} id @returns {Promise<{ post: BlogPostRow }>} */
export const adminGetPost = (id) => api.get(`/admin/blog/${idPath(id)}`)

/** @param {BlogPostBody} body @returns {Promise<{ post: BlogPostRow }>} 409 when the slug exists. */
export const adminCreatePost = (body) => api.post('/admin/blog', body)

/**
 * @param {number | string} id
 * @param {BlogPostBody} body partial
 * @returns {Promise<{ post: BlogPostRow }>}
 */
export const adminUpdatePost = (id, body) => api.patch(`/admin/blog/${idPath(id)}`, body)

/** @param {number | string} id @returns {Promise<{ message: string }>} */
export const adminDeletePost = (id) => api.delete(`/admin/blog/${idPath(id)}`)

/**
 * Render markdown with the server's renderer (same sanitizer as the stored `content_html`).
 * @param {string} content
 * @returns {Promise<{ html: string }>}
 */
export const adminPreview = (content) => api.post('/admin/blog/preview', { content })

/** @returns {Promise<BlogSettingsResponse>} */
export const adminGetSettings = () => api.get('/admin/blog/settings')

/** @param {BlogSettingsPatch} patch @returns {Promise<{ settings: BlogSettings }>} */
export const adminUpdateSettings = (patch) => api.patch('/admin/blog/settings', patch)

/**
 * Queue an article generation run (202). 409 when a run is already in progress.
 * @param {{ topic?: string, keyword?: string, publish?: boolean }} [input]
 * @returns {Promise<{ run: BlogRun }>}
 */
export const adminGenerate = (input = {}) => api.post('/admin/blog/generate', input)

/** @param {number} [limit] @returns {Promise<{ runs: BlogRun[] }>} */
export const adminListRuns = (limit = 20) => api.get(`/admin/blog/runs${qs({ limit })}`)

/** @param {number | string} id @returns {Promise<{ run: BlogRun }>} */
export const adminGetRun = (id) => api.get(`/admin/blog/runs/${idPath(id)}`)

/**
 * Regenerate hero / figures / thumbnail / OG for an existing post in the background (202).
 * @param {number | string} id
 * @param {Exclude<BlogImageProvider, 'auto'>} [provider]
 * @returns {Promise<{ run: BlogRun }>}
 */
export const adminRegenerateImages = (id, provider) =>
  api.post(`/admin/blog/${idPath(id)}/images/regenerate`, provider ? { provider } : {})

/**
 * Store a cover (by URL or base64), deriving the thumbnail and OG image.
 * @param {number | string} id
 * @param {BlogCoverInput} input
 * @returns {Promise<{ post: BlogPostRow }>}
 */
export const adminUploadCover = (id, input) => api.post(`/admin/blog/${idPath(id)}/cover`, input)

/** @param {number | string} id @returns {Promise<{ post: BlogPostRow }>} */
export const adminPublishPost = (id) => api.post(`/admin/blog/${idPath(id)}/publish`, {})

/** @param {number | string} id @returns {Promise<{ post: BlogPostRow }>} */
export const adminUnpublishPost = (id) => api.post(`/admin/blog/${idPath(id)}/unpublish`, {})
